/**
 * Pure NPC schedule interpreter (no Phaser). Executes NpcScheduleStep lists on the fixed step against
 * any object that satisfies ScheduleActorLike, so it can be unit-tested with a fake actor.
 *
 * Step semantics (all deterministic):
 *  - wait  { ms }                 waits ms ± jitter (jitter drawn from the per-npc SeededRng fork)
 *  - walk  { x, y }               walkTo(x, y) then waits until the actor reports !isMoving()
 *  - face  { facing }             setFacing, continues in the same step
 *  - emote { emote, ms? }         showEmote then waits ms (default 1200)
 *  - loop                          restarts from step 0; a list without loop ends idle
 */
import type { NpcScheduleStep } from '@/core/types/contracts';
import type { Facing } from '@/core/types';
import type { SeededRng } from '@/core/rng';

export interface ScheduleActorLike {
  readonly x: number;
  readonly y: number;
  facing: Facing;
  setFacing(f: Facing): void;
  walkTo(x: number, y: number, speedPxPerSec?: number): unknown;
  isMoving(): boolean;
  stop(): void;
  showEmote(emote: string, ms?: number): unknown;
}

export interface ScheduleOptions {
  /** Fraction of a wait's ms used as ± jitter range (default 0.2). */
  jitter?: number;
  /** Walk speed passed to walkTo (default: actor default). */
  walkSpeed?: number;
}

type Phase = 'ready' | 'waiting' | 'walking' | 'emoting' | 'done';

export class ScheduleProgram {
  private steps: NpcScheduleStep[];
  private index = 0;
  private phase: Phase = 'ready';
  private remainingMs = 0;
  private walkTarget: { x: number; y: number } | null = null;
  private paused = false;
  private resumeWalk = false;
  private readonly jitter: number;

  constructor(steps: NpcScheduleStep[], private readonly rng: SeededRng, private readonly opts: ScheduleOptions = {}) {
    this.steps = [...steps];
    this.jitter = opts.jitter ?? 0.2;
    if (this.steps.length === 0) this.phase = 'done';
  }

  get currentIndex(): number { return this.index; }
  get currentPhase(): Phase { return this.phase; }
  get isPaused(): boolean { return this.paused; }

  /** Replace the step list (weather behaviour swap); restarts from step 0. */
  setSteps(steps: NpcScheduleStep[], actor?: ScheduleActorLike): void {
    if (this.phase === 'walking' && actor) actor.stop();
    this.steps = [...steps];
    this.index = 0;
    this.remainingMs = 0;
    this.walkTarget = null;
    this.resumeWalk = false;
    this.phase = this.steps.length === 0 ? 'done' : 'ready';
  }

  /** Freeze (dialogue/cinematics). A walk in progress is stopped and re-issued on resume. */
  setPaused(on: boolean, actor: ScheduleActorLike): void {
    if (on === this.paused) return;
    this.paused = on;
    if (on && this.phase === 'walking') { actor.stop(); this.resumeWalk = true; }
    if (!on && this.resumeWalk && this.walkTarget) {
      actor.walkTo(this.walkTarget.x, this.walkTarget.y, this.opts.walkSpeed);
      this.resumeWalk = false;
    }
  }

  step(actor: ScheduleActorLike, dtMs: number): void {
    if (this.paused || this.phase === 'done') return;
    // Bounded loop so consecutive non-blocking steps (face/loop) resolve in the same tick.
    for (let guard = 0; guard < 32; guard++) {
      switch (this.phase) {
        case 'waiting':
        case 'emoting':
          this.remainingMs -= dtMs;
          if (this.remainingMs > 0) return;
          this.advance();
          break;
        case 'walking':
          if (actor.isMoving()) return;
          this.walkTarget = null;
          this.advance();
          break;
        case 'ready': {
          const s = this.steps[this.index];
          if (!s) { this.phase = 'done'; return; }
          switch (s.kind) {
            case 'wait': {
              const ms = Math.max(0, s.ms ?? 1000);
              const j = Math.round(ms * this.jitter);
              this.remainingMs = ms + (j > 0 ? this.rng.int(-j, j) : 0);
              this.phase = 'waiting';
              if (this.remainingMs <= 0) { this.advance(); break; }
              return;
            }
            case 'walk': {
              const x = s.x ?? actor.x, y = s.y ?? actor.y;
              this.walkTarget = { x, y };
              actor.walkTo(x, y, this.opts.walkSpeed);
              this.phase = 'walking';
              return;
            }
            case 'face':
              if (s.facing) actor.setFacing(s.facing);
              this.advance();
              break;
            case 'emote': {
              const ms = s.ms ?? 1200;
              actor.showEmote(s.emote ?? 'exclaim', ms);
              this.remainingMs = ms;
              this.phase = 'emoting';
              return;
            }
            case 'loop':
              this.index = 0;
              this.phase = 'ready';
              if (this.steps.length === 1) { this.phase = 'done'; return; } // a lone loop would spin
              break;
            default:
              this.advance();
          }
          break;
        }
        default:
          return;
      }
    }
  }

  private advance(): void {
    this.index++;
    this.phase = this.index < this.steps.length ? 'ready' : 'done';
  }
}
